/**
 * Country/timezone-aware "today" date label.
 * Renders server-side (SSR) so Google sees a real date string in the HTML
 * — not a hydration-only value. SSR runs in UTC; we explicitly format in
 * the target country's local timezone so a rate page rendered at 23:00 UTC
 * in Pakistan (04:00 PKT) shows the local date, not the UTC date.
 */

export type TzKey =
  | "pakistan"
  | "india"
  | "uae"
  | "saudi"
  | "us"
  | "uk"
  | "global";

const TZ: Record<TzKey, string> = {
  pakistan: "Asia/Karachi",
  india: "Asia/Kolkata",
  uae: "Asia/Dubai",
  saudi: "Asia/Riyadh",
  us: "America/New_York",
  uk: "Europe/London",
  global: "UTC",
};

/** "28 June 2026" — used in titles + H1 subheads. */
export function todayLocal(tz: TzKey = "global"): string {
  return new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: TZ[tz],
  });
}

/** "Sunday, June 28, 2026" — used in body intros, contrasts with title. */
export function todayLocalLong(tz: TzKey = "global"): string {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: TZ[tz],
  });
}

/** "28-june-2026" — anchor-safe slug for per-section date URLs. */
export function todayLocalSlug(tz: TzKey = "global"): string {
  return todayLocal(tz).toLowerCase().replace(/\s+/g, "-");
}

/** Country-to-tz convenience. */
export function tzForCountry(c: string): TzKey {
  const k = c.toLowerCase();
  if (k === "pakistan" || k === "pk") return "pakistan";
  if (k === "india" || k === "in") return "india";
  if (k === "uae" || k === "ae") return "uae";
  if (k === "saudi" || k === "saudi-arabia" || k === "sa") return "saudi";
  if (k === "us") return "us";
  if (k === "uk" || k === "gb") return "uk";
  return "global";
}